<template>
  <div class="ManagePermissions">
    <t-card title="管理权限组" bordered>
      <div class="page-layout">
        <!-- 左侧：权限组列表 -->
        <div class="permission-table">
          <t-table
            ref="permissionTable"
            row-key="promissID"
            :data="permissionGroups"
            :columns="columns"
            :bordered="true"
            :hover="true"
            table-layout="fixed"
          >
            <template #gameids="{ row }">
              <span>{{ getGameTitles(row.gameids) }}</span>
            </template>

            <template #operation="{ row }">
              <t-space>
                <t-button
                  theme="primary"
                  size="small"
                  @click="openEditDialog(row)"
                >
                  编辑
                </t-button>

                <t-button
                  theme="danger"
                  size="small"
                  @click="deleteGroup(row.promissID)"
                >
                  删除
                </t-button>
              </t-space>
            </template>
          </t-table>
        </div>

        <!-- 右侧：添加权限组表单 -->
        <div class="form-container">
          <t-card title="添加权限组" bordered>
            <t-form
              :data="newGroup"
              label-width="100px"
            >
              <t-form-item label="权限组名称" name="name">
                <t-input
                  v-model="newGroup.name"
                  placeholder="请输入权限组名称"
                  clearable
                />
              </t-form-item>

              <t-form-item label="选择游戏" name="gameids">
                <div class="game-select-row">
                  <t-button theme="primary" @click="openGameSelector(false)">
                    选择游戏
                  </t-button>

                  <span v-if="newGroup.gameids.length" class="selected-games">
                    已选择游戏：{{ getGameTitles(newGroup.gameids) }}
                  </span>

                  <span v-else class="empty-text">
                    暂未选择游戏
                  </span>
                </div>
              </t-form-item>

              <t-form-item>
                <t-button theme="primary" @click="addPermissionGroup">
                  添加
                </t-button>
              </t-form-item>
            </t-form>
          </t-card>
        </div>
      </div>
    </t-card>

    <!-- 编辑权限组弹窗 -->
    <t-dialog
      v-model:visible="dialogVisible"
      header="编辑权限组"
      width="520px"
      :confirm-btn="null"
      :cancel-btn="null"
      @close="resetForm"
    >
      <t-form
        :data="currentGroup"
        label-width="100px"
      >
        <t-form-item label="权限组名称" name="name">
          <t-input
            v-model="currentGroup.name"
            placeholder="请输入权限组名称"
            clearable
          />
        </t-form-item>

        <t-form-item label="选择游戏" name="gameids">
          <div class="game-select-row">
            <t-button theme="primary" @click="openGameSelector(true)">
              选择游戏
            </t-button>

            <span v-if="currentGroup.gameids.length" class="selected-games">
              已选择游戏：{{ getGameTitles(currentGroup.gameids) }}
            </span>

            <span v-else class="empty-text">
              暂未选择游戏
            </span>
          </div>
        </t-form-item>
      </t-form>

      <template #footer>
        <t-space>
          <t-button theme="default" @click="dialogVisible = false">
            取消
          </t-button>

          <t-button theme="primary" @click="updatePermissionGroup">
            保存
          </t-button>
        </t-space>
      </template>
    </t-dialog>

    <!-- 选择游戏弹窗 -->
    <t-dialog
      v-model:visible="gameSelectorVisible"
      header="选择游戏"
      width="680px"
      :confirm-btn="null"
      :cancel-btn="null"
    >
      <div class="game-selector">
        <t-checkbox-group v-model="selectedGameIDs">
          <div class="game-checkbox-list">
            <t-checkbox
              v-for="game in allGames"
              :key="game.gameid"
              :value="String(game.gameid)"
            >
              {{ game.title }}
            </t-checkbox>
          </div>
        </t-checkbox-group>
      </div>

      <template #footer>
        <t-space>
          <t-button theme="default" @click="gameSelectorVisible = false">
            取消
          </t-button>

          <t-button theme="primary" @click="confirmGameSelection">
            确定
          </t-button>
        </t-space>
      </template>
    </t-dialog>
  </div>
</template>

<script>
import axios from '../axios';
import { MessagePlugin } from 'tdesign-vue-next';

export default {
  name: 'ManagePermissions',

  data() {
    return {
      columns: [
        {
          colKey: 'promissID',
          title: '权限组ID',
          width: 120
        },
        {
          colKey: 'name',
          title: '权限组名称',
          width: 160
        },
        {
          colKey: 'gameids',
          title: '游戏列表',
          minWidth: 220
        },
        {
          colKey: 'operation',
          title: '操作',
          width: 160,
          fixed: 'right'
        }
      ],

      permissionGroups: [],

      newGroup: {
        name: '',
        gameids: []
      },

      allGames: [],

      dialogVisible: false,
      gameSelectorVisible: false,

      currentGroup: {
        promissID: '',
        name: '',
        gameids: []
      },

      selectedGameIDs: [],

      selectorMode: 'add'
    };
  },

  methods: {
    normalizeGameids(gameid) {
      if (!gameid) {
        return [];
      }

      if (Array.isArray(gameid)) {
        return gameid.map(item => String(item)).filter(Boolean);
      }

      return String(gameid)
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);
    },

    async fetchPermissionGroups() {
      try {
        const token = localStorage.getItem('user-token');

        const response = await axios.post('/index.php?action=getPromissGroups', {
          token
        });

        if (response.data.code === 200) {
          this.permissionGroups = response.data.groups.map(group => ({
            ...group,
            gameids: this.normalizeGameids(group.gameid)
          }));
        } else {
          MessagePlugin.error(response.data.message || '获取权限组列表失败');
        }
      } catch (error) {
        console.error('Error fetching permission groups:', error.response || error);
        MessagePlugin.error('获取权限组列表失败');
      }
    },

    async fetchAllGames() {
      try {
        const token = localStorage.getItem('user-token');

        const response = await axios.post('/index.php?action=getGames_make', {
          token
        });

        if (response.data.code === 200) {
          this.allGames = response.data.games.map(game => ({
            ...game,
            gameid: String(game.gameid)
          }));
        } else {
          MessagePlugin.error(response.data.message || '获取游戏列表失败');
        }
      } catch (error) {
        console.error('Error fetching all games:', error.response || error);
        MessagePlugin.error('获取游戏列表失败');
      }
    },

    openGameSelector(isEdit = false) {
      this.selectorMode = isEdit ? 'edit' : 'add';

      this.selectedGameIDs = isEdit
        ? [...this.currentGroup.gameids].map(item => String(item))
        : [...this.newGroup.gameids].map(item => String(item));

      this.gameSelectorVisible = true;
    },

    confirmGameSelection() {
      if (this.selectorMode === 'edit') {
        this.currentGroup.gameids = [...this.selectedGameIDs];
      } else {
        this.newGroup.gameids = [...this.selectedGameIDs];
      }

      this.gameSelectorVisible = false;
    },

    getGameTitles(gameids) {
      const ids = this.normalizeGameids(gameids);

      if (!ids.length) {
        return '无';
      }

      return ids
        .map(gameid => {
          const game = this.allGames.find(g => String(g.gameid) === String(gameid));
          return game ? game.title : gameid;
        })
        .join(', ');
    },

    async addPermissionGroup() {
      if (!this.newGroup.name.trim()) {
        MessagePlugin.warning('请输入权限组名称');
        return;
      }

      if (!this.newGroup.gameids.length) {
        MessagePlugin.warning('请选择游戏');
        return;
      }

      try {
        const token = localStorage.getItem('user-token');
        const gameids = this.newGroup.gameids.join(',');

        const response = await axios.post('/index.php?action=addPromissGroup', {
          token,
          name: this.newGroup.name,
          gameids
        });

        if (response.data.code === 200) {
          await this.fetchPermissionGroups();

          this.newGroup.name = '';
          this.newGroup.gameids = [];

          MessagePlugin.success('权限组添加成功');
        } else {
          MessagePlugin.error(response.data.message || '添加权限组失败');
        }
      } catch (error) {
        console.error('Error adding permission group:', error.response || error);
        MessagePlugin.error('添加权限组失败');
      }
    },

    async deleteGroup(promissID) {
      try {
        const token = localStorage.getItem('user-token');

        const response = await axios.post('/index.php?action=deletePromissGroup', {
          token,
          promissID
        });

        if (response.data.code === 200) {
          await this.fetchPermissionGroups();
          MessagePlugin.success('权限组删除成功');
        } else {
          MessagePlugin.error(response.data.message || '删除权限组失败');
        }
      } catch (error) {
        console.error('Error deleting permission group:', error.response || error);
        MessagePlugin.error('删除权限组失败');
      }
    },

    openEditDialog(group) {
      this.currentGroup = {
        ...group,
        gameids: this.normalizeGameids(group.gameids)
      };

      this.selectedGameIDs = [...this.currentGroup.gameids];
      this.dialogVisible = true;
    },

    async updatePermissionGroup() {
      if (!this.currentGroup.name.trim()) {
        MessagePlugin.warning('请输入权限组名称');
        return;
      }

      if (!this.currentGroup.gameids.length) {
        MessagePlugin.warning('请选择游戏');
        return;
      }

      try {
        const token = localStorage.getItem('user-token');
        const gameids = this.currentGroup.gameids.join(',');

        const response = await axios.post('/index.php?action=updatePromissGroup', {
          token,
          promissID: this.currentGroup.promissID,
          name: this.currentGroup.name,
          gameids
        });

        if (response.data.code === 200) {
          await this.fetchPermissionGroups();

          this.dialogVisible = false;

          MessagePlugin.success('权限组更新成功');
        } else {
          MessagePlugin.error(response.data.message || '更新权限组失败');
        }
      } catch (error) {
        console.error('Error updating permission group:', error.response || error);
        MessagePlugin.error('更新权限组失败');
      }
    },

    resetForm() {
      this.currentGroup = {
        promissID: '',
        name: '',
        gameids: []
      };

      this.selectedGameIDs = [];
      this.selectorMode = 'add';
    }
  },

  mounted() {
    this.fetchPermissionGroups();
    this.fetchAllGames();
  }
};
</script>

<style scoped>
.ManagePermissions {
  padding: 20px;
}

.page-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 420px;
  gap: 20px;
  align-items: flex-start;
}

.permission-table {
  min-width: 0;
}

.form-container {
  min-width: 0;
}

.game-select-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.selected-games {
  color: var(--td-text-color-secondary);
  line-height: 22px;
}

.empty-text {
  color: var(--td-text-color-placeholder);
}

.game-selector {
  max-height: 420px;
  overflow-y: auto;
  padding-right: 4px;
}

.game-checkbox-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
}

@media screen and (max-width: 1024px) {
  .page-layout {
    grid-template-columns: 1fr;
  }
}

@media screen and (max-width: 768px) {
  .ManagePermissions {
    padding: 12px;
  }

  .game-checkbox-list {
    grid-template-columns: 1fr;
  }
}
</style>